import { requestMapperErrors } from './errorSchema/requestMapperErrors.js';
import { responseMapperErrors } from './errorSchema/responseMapperErrors.js';
import { argumentErrors } from './errorSchema/argumentErrors.js';
import { authErrors } from './errorSchema/authErrors.js';
import { sqlErrors } from './errorSchema/sqlErrors.js';
import { ftpErrors } from './errorSchema/ftpErrors.js';

export const errorSchema = {
  ...requestMapperErrors,
  ...responseMapperErrors,
  ...argumentErrors,
  ...authErrors,
  ...sqlErrors,
  ...ftpErrors,
  400: {
    status: 400,
    code: '400_BAD_REQUEST',
    title: 'Bad Request',
    subtitle: 'HTTP STATUS 400',
    description: 'The request was rejected due to incorrect data or misconfiguration. Please review the input data or contact support for assistance.',
    about: {
      title: 'More on this error',
      description: 'The server cannot process the request due to a client error, such as malformed syntax, excessive size, invalid framing, or deceptive routing.',
      link: { text: 'Learn More', url: 'https://en.wikipedia.org/wiki/List_of_HTTP_status_codes#400' }
    },
    component: 'DefaultErrorPage'
  },
  401: {
    status: 401,
    code: '401_UNAUTHORIZED',
    title: 'Unauthorized',
    subtitle: 'HTTP STATUS 401',
    description: 'This action could not complete due to an authentication issue. This typically occurs when credentials are missing, incorrect, or expired. Please double-check your credentials or try logging in again.',
    about: {
      title: 'More on this error',
      description: 'This error occurs when authentication is required but has failed or has not been provided. The server response must include a WWW-Authenticate header field containing a challenge applicable to the requested resource.',
      link: { text: 'Learn More', url: 'https://en.wikipedia.org/wiki/List_of_HTTP_status_codes#401' }
    },
    roles: {
      admin: {
        about: {
          title: 'More on this error',
          description: 'This error indicates that the request lacks valid authentication credentials. Review the app’s authentication setup and ensure that the credentials provided are valid.',
          link: { text: 'Learn More', url: 'https://docs.mindcloud.co/docs/auth-fundamentals' }
        }
      }
    },
    component: 'DefaultErrorPage'
  },
  404: {
    status: 404,
    code: '404_NOT_FOUND',
    title: 'Resource Not Found',
    subtitle: 'HTTP STATUS 404',
    description: 'This action could not be completed because the resource could not be found. It may have been deleted, moved, or the URL is incorrect. Please verify the resource and try again.',
    about: {
      title: 'More on this error',
      description: 'The server cannot find the requested resource. In a browser, this typically means the URL is incorrect or the resource no longer exists. In an API, this can also indicate that the endpoint is valid but the resource is missing or unavailable.',
      link: { text: 'Learn More', url: 'https://en.wikipedia.org/wiki/HTTP_404' }
    },
    component: 'DefaultErrorPage'
  },
  429: {
    status: 429,
    code: '429_TOO_MANY_REQUESTS',
    title: 'Too Many Requests',
    subtitle: 'HTTP STATUS 429',
    description: 'This action could not complete because your account has made too many requests to this resource. The system retried 3 times, but the request was still rejected. Please consider adjusting your Workflow to reduce the frequency of this action.',
    about: {
      title: 'More on this error',
      description: 'HTTP status code 429 indicates that the client has sent too many requests in a short period of time, known as "rate limiting." The server may provide a Retry-After header indicating when the client can try again.',
      link: { text: 'Learn More', url: 'https://en.wikipedia.org/wiki/Rate_limiting' }
    },
    roles: {
      admin: {
        fields: {
          'Retry Settings': ['delay', 'maxAttempts', 'multiplier']
        },
        about: {
          title: 'More on this error',
          description: 'To address rate limiting, consider adjusting the retry settings in your Workflow. These settings include "delay" (time between retries), "maxAttempts" (maximum number of retries), and "multiplier" (exponential backoff factor).',
          link: { text: 'Learn More', url: 'https://docs.mindcloud.co/docs/rate-limiting' }
        }
      }
    },
    component: 'DefaultErrorPage'
  },
  EMPTY_PAYLOAD: {
    status: 400,
    code: 'EMPTY_PAYLOAD',
    title: 'Empty Payload',
    subtitle: 'MISSING REQUIRED DATA',
    description: 'It seems the system didn’t receive any data to process for this step. This could happen if required fields were left empty or data wasn’t passed correctly. Please review the input and ensure all necessary information is provided before running the step again.',
    about: {
      title: 'More on this error',
      description: 'A "payload" is the data your step sends to the system to be processed. When it’s missing or empty, the system cannot proceed with the requested action. This error typically occurs when essential information is left out or misconfigured in the step setup.',
      link: { text: 'Learn More', url: 'https://en.wikipedia.org/wiki/Payload_(computing)' }
    },
    component: 'DefaultErrorPage'
  },
  EMPTY_MAPPER_EXCEPTION: {
    status: 500,
    code: 'EMPTY_MAPPER_EXCEPTION',
    title: 'Response Error',
    subtitle: 'EMPTY RESPONSE RESULT',
    description: 'The system encountered an issue where the step returned empty data. Please try again or contact support if the issue persists.',
    roles: {
      admin: {
        title: 'Mapper Error',
        subtitle: 'EMPTY MAPPER RESULT',
        description: 'The mapper used in this step returned empty data. This can happen if the Request Mapper didn’t structure the payload properly or if the Response Mapper failed to interpret the data from the API. Review the request and response configurations for correct data formats.'
      }
    },
    component: 'DefaultErrorPage'
  },
  MAPPER_ERROR: {
    status: 500,
    code: 'MAPPER_ERROR',
    title: 'System Error',
    subtitle: 'PROCESSING FAILURE',
    description: 'The system encountered an issue while processing this step. Please contact support for further assistance.',
    roles: {
      admin: {
        title: 'Mapper Error',
        subtitle: 'MAPPER EVALUATION FAILURE',
        description: "The system encountered an issue while trying to run this step's mapper. This error typically occurs due to a bug in the mapper code or an unexpected input. Please review the response details for further information and check the mapper configuration to ensure it is handling data formats correctly.",
        about: {
          title: 'More on this error',
          description: 'Mappers are used to transform data between different formats. This error may arise from an internal bug or unexpected input data that the mapper cannot process. Review the mapper logic and configuration to troubleshoot the issue.'
        }
      }
    },
    component: 'DefaultErrorPage',
    showResponseBody: true
  },
  UNSAFE_CODE: {
    status: 400,
    code: 'UNSAFE_CODE',
    title: 'Unsafe Code Detected',
    subtitle: 'SECURITY RESTRICTION',
    description: 'This code contains restricted operations and cannot be executed for security reasons. Please review your code and ensure it follows our security guidelines.',
    component: 'DefaultErrorPage'
  },
  MAPPER_OUTPUT_SIZE_RESTRICTION: {
    status: 500,
    code: 'MAPPER_OUTPUT_SIZE_RESTRICTION',
    title: 'Mapper Output Size Restriction',
    subtitle: 'INEFFICIENT MAPPER STRUCTURE DETECTED',
    description: 'Your mapper output appears to be structured as one very large row instead of multiple smaller rows. This pattern can be inefficient for data processing and has hit our output size limit. \n\nIf this structure is intentional and you need to process large datasets this way, you can click the button down below. Otherwise, consider refactoring your mapper to return multiple rows for better performance.',
    actions: ['bypassOutputSizeRestriction'],
    component: 'DefaultErrorPage',
    showResponseBody: true
  },
  FATAL_ERROR: {
    status: 500,
    code: 'FATAL_ERROR',
    title: 'Workflow Has Crashed',
    subtitle: 'PROCESSING FAILURE',
    description: 'The system encountered a fatal error and had to stop the workflow.',
    component: 'DefaultErrorPage',
    showResponseBody: true
  },
  INVALID_DATA_FORMAT: {
    status: 500,
    code: 'INVALID_DATA_FORMAT',
    title: 'Invalid Output Format',
    subtitle: 'APP ERROR',
    description: 'This error occurred due to a misconfigured App. MindCloud Apps are set up internally. If you encounter this issue, you can report it in the MindCloud Slack Channel for assistance.',
    roles: {
      admin: {
        description: 'This step attempted to map data but returned it in an unexpected format. The system expected the data to be an array, but it was received in a different format. This suggests that the Response Mapper for this API needs to be corrected.',
        about: {
          title: 'More on this error',
          description: 'This error occurs when the App is not configured correctly. After receiving data from Slack (or another resource), the system uses an API Response Mapper to standardize the data format. In this case, the system expected an array but received something else, leading to this error.',
          link: { text: 'Learn More', url: 'https://mindcloud.document360.io/docs/gravity-standard-response-format' }
        }
      }
    },
    component: 'DefaultErrorPage'
  },
  INVALID_APP: {
    status: 400,
    code: 'INVALID_APP',
    title: 'Invalid App',
    subtitle: 'INVALID APP SELECTION',
    description: 'The app selected for this step is either missing or not available. Please contact support if you believe this is an error or try selecting another app.',
    roles: {
      admin: {
        description: 'The app selected for this step is either missing or incorrectly configured. Platform Apps are global integrations with external systems, while Function Apps manage internal utilities and workflow controls. Please review the app configuration or select a valid app to continue.',
        link: { text: 'Learn More', url: 'https://docs.mindcloud.co/docs/gravity-apps-overview' }
      }
    },
    component: 'DefaultErrorPage'
  },
  INVALID_ACTION: {
    status: 500,
    code: 'INVALID_ACTION',
    title: 'Invalid Action',
    subtitle: 'INVALID ACTION SELECTION',
    description: 'The action selected for this step is invalid or missing. Please try another action or contact support for assistance.',
    roles: {
      admin: {
        description: 'The action selected for this step is either invalid or misconfigured. Actions are extensions of REST APIs, so this error may occur due to incorrect configuration such as missing headers, arguments, or response mappings. Please review the action setup to proceed.',
        link: { text: 'Learn More', url: 'https://docs.mindcloud.co/docs/actions-overview' }
      }
    },
    component: 'DefaultErrorPage'
  },
  INVALID_CREDENTIALS: {
    status: 401,
    code: 'INVALID_CREDENTIALS',
    title: 'Invalid Connection',
    subtitle: 'CONNECTION VALIDATION FAILURE',
    description: 'The connection provided for authentication are either invalid or missing. This can occur if the API key, token, or other required values are incorrect or not supplied. Please review the connection setup and ensure that all authentication details are correctly configured.',
    component: 'DefaultErrorPage'
  },
  MISSING_PROVISIONING_CREDENTIAL: {
    status: 400,
    code: 'MISSING_PROVISIONING_CREDENTIAL',
    title: 'Provision Connection Not Found',
    subtitle: 'MISSING PROVISIONING CREDENTIAL',
    description: 'This step requires a provisioned connection that is missing. Please contact support for further assistance.',
    component: 'DefaultErrorPage'
  },
  INFISICAL_GET_SECRET_FAILED: {
    status: 500,
    code: 'INFISICAL_GET_SECRET_FAILED',
    title: 'Failed to Retrieve Connection',
    subtitle: 'CONNECTION RETRIEVAL FAILURE',
    description: 'The system encountered an issue while attempting to retrieve the necessary connection. Please contact support for further assistance.',
    roles: {
      admin: {
        title: 'Failed to Retrieve Infisical Secret',
        subtitle: 'INFISICAL SECRET RETRIEVAL FAILURE',
        description: 'The system was unable to retrieve the secret from Infisical. This could be due to missing credentials, an incorrect secret reference, or a connectivity issue with Infisical. Please verify the secret configuration and ensure that all required credentials are correctly set up.'
      }
    },
    component: 'DefaultErrorPage'
  },
  MAXIMUM_STACK_SIZE_EXCEEDED: {
    status: 500,
    code: 'MAXIMUM_STACK_SIZE_EXCEEDED',
    title: 'Maximum Call Stack Size Exceeded',
    subtitle: 'INFINITE LOOP DETECTED',
    description: 'The system has detected that a step is running repeatedly, which may indicate an infinite loop. This can happen when a function or step keeps calling itself without a stop condition. Please contact support to resolve the issue.',
    about: {
      title: 'More on this error',
      description: 'Infinite loops can cause a system to run indefinitely without reaching a conclusion, eventually leading to a maximum call stack size being exceeded. This often happens when a recursive function lacks a proper exit condition.',
      link: { text: 'Learn More', url: 'https://en.wikipedia.org/wiki/Infinite_loop' }
    },
    roles: {
      admin: {
        title: 'Maximum Call Stack Size Exceeded',
        subtitle: 'INFINITE LOOP DETECTED',
        description: 'This error occurs when the system detects a function or process that is continuously calling itself, potentially leading to an infinite loop. Infinite loops can exhaust system resources, causing the call stack to overflow. Review the mapper logic and function calls to ensure there is a valid stop condition to prevent recursion.'
      }
    },
    component: 'DefaultErrorPage'
  },
  STEP_CANCELLED: {
    status: 499,
    code: 'STEP_CANCELLED',
    title: 'This action was cancelled',
    subtitle: 'ACTION CANCELLED',
    description: 'You have manually cancelled this action before it completed.',
    component: 'DefaultErrorPage',
    showResponseBody: false
  },
  WORKFLOW_CANCELLED: {
    status: 499,
    code: 'WORKFLOW_CANCELLED',
    title: 'This workflow was cancelled',
    subtitle: 'WORKFLOW CANCELLED',
    description: 'You have manually cancelled this workflow before it completed.',
    component: 'DefaultErrorPage',
    showResponseBody: false
  },
  WORKFLOW_ID_MISSING: {
    status: 400,
    code: 'WORKFLOW_ID_MISSING',
    title: 'Workflow ID Missing',
    subtitle: 'MISSING REQUIRED DATA',
    description: 'Please add a workflow ID to the step and try again.',
    component: 'DefaultErrorPage'
  },
  SQS_MESSAGE_SEND_ERROR: {
    status: 500,
    code: 'SQS_MESSAGE_SEND_ERROR',
    title: 'SQS Message Send Error',
    subtitle: 'SQS MESSAGE SEND ERROR',
    description: 'The system encountered an issue while attempting to send a message to SQS.',
    component: 'DefaultErrorPage',
    showResponseBody: true
  },
  INVALID_OPERATOR: {
    status: 500,
    code: 'INVALID_OPERATOR',
    title: 'Invalid Operator',
    subtitle: 'INVALID OPERATOR SELECTION',
    description: 'The operation could not be executed due to an invalid or missing operator configuration. Please verify the operator selection in your step settings and ensure it aligns with the expected operation type.',
    component: 'DefaultErrorPage'
  },
  INTERRUPTED_BY_TIMEOUT: {
    status: 400,
    code: 'INTERRUPTED_BY_TIMEOUT',
    title: 'Interrupted by timeout',
    subtitle: 'TIMEOUT ISSUE',
    description: 'Workflow interrupted after exceeding 4 hours of execution time.',
    component: 'DefaultErrorPage',
    showResponseBody: true
  },
  FUNCTION_WORKFLOW_NOT_FOUND: {
    status: 500,
    code: 'FUNCTION_WORKFLOW_NOT_FOUND',
    title: 'Function Workflow Not Found',
    subtitle: 'FUNCTION RESOLUTION ERROR',
    description: 'The referenced function workflow could not be resolved. This may occur if the function has been deleted or is unavailable in the current environment. Please verify the function exists and is accessible.',
    component: 'DefaultErrorPage',
    showResponseBody: true
  },
  INCOMPLETE_WORKFLOW: {
    status: 500,
    code: 'INCOMPLETE_WORKFLOW',
    title: 'Incomplete Workflow Execution',
    subtitle: 'INCOMPLETE WORKFLOW',
    description: 'The workflow did not complete successfully, resulting in an incomplete timeline. ',
    component: 'DefaultErrorPage',
    showResponseBody: false
  },
  NETSUITE_COLUMNS_COLLECT_STEP_REQUIRED: {
    status: 409,
    code: 'NETSUITE_COLUMNS_COLLECT_STEP_REQUIRED',
    title: 'NetSuite Columns Need Refresh',
    subtitle: 'NETSUITE COLUMNS NEED REFRESH',
    description: 'NetSuite column metadata is missing for this record type. Re-run the collect step from the Deploy tab.',
    component: 'DefaultErrorPage',
    showResponseBody: false
  },
  DEFAULT: {
    status: 500,
    code: 'DEFAULT',
    title: 'An Error Occurred',
    subtitle: 'UNKNOWN ERROR',
    description: 'An unexpected error occurred while attempting to complete this action. Please try again later or contact support if the issue persists.',
    component: 'DefaultErrorPage',
    showResponseBody: true
  }
};
